import { useSkipFutureAlerts } from "@jugl-web/domain-resources/common/hooks/useSkipFutureAlerts";
import { useTicketStatuses } from "@jugl-web/domain-resources/tickets/hooks/useTicketStatuses";
import { TicketDetailsModel, UpdateTicketPayload } from "@jugl-web/rest-api";
import { TicketStatus } from "@jugl-web/rest-api/tickets/models/common-types";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { TableCell } from "@jugl-web/ui-kit";
import { useToast } from "@jugl-web/utils";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { RESOLVE_TICKET_ALERT_ID } from "@jugl-web/utils/storage";
import { ResolveTicketDialog } from "@web-src/modules/client-support/components/ResolveTicketDialog";
import { TicketPropertyPill } from "@jugl-web/domain-resources/tickets/components/TicketPropertyPill";
import { TicketStatusPickerPopover } from "@web-src/modules/client-support/components/TicketStatusPickerPopover";
import { FC, useCallback, useMemo } from "react";

interface StatusCellContentProps {
  ticket: TicketListItemModel;
  onUpdate: (
    data: UpdateTicketPayload
  ) => Promise<TicketDetailsModel | undefined>;
}

export const StatusCellContent: FC<StatusCellContentProps> = ({
  ticket,
  onUpdate,
}) => {
  const { getStatusById } = useTicketStatuses();
  const { toast } = useToast();

  const { areFutureAlertsSkipped, onSkipFutureAlertsChange } =
    useSkipFutureAlerts();

  const resolveTicketConfirmationDialogState = useConfirmationDialogState();

  const statusDetails = useMemo(
    () => getStatusById(ticket.status),
    [ticket.status, getStatusById]
  );

  const handleStatusChange = useCallback(
    async (status: TicketStatus) => {
      if (status !== "resolved") {
        onUpdate({ status });
        return;
      }

      if (areFutureAlertsSkipped(RESOLVE_TICKET_ALERT_ID)) {
        const updatedTicket = await onUpdate({ status: "resolved" });

        if (updatedTicket) {
          toast("Ticket has been resolved");
        }

        return;
      }

      resolveTicketConfirmationDialogState.open();
    },
    [
      areFutureAlertsSkipped,
      onUpdate,
      resolveTicketConfirmationDialogState,
      toast,
    ]
  );

  const handleConfirmResolveTicket = useCallback(
    async (shouldSkipFutureAlerts: boolean) => {
      const updatedTicket = await onUpdate({ status: "resolved" });

      if (!updatedTicket) {
        return;
      }

      if (shouldSkipFutureAlerts) {
        onSkipFutureAlertsChange(RESOLVE_TICKET_ALERT_ID, true);
      }

      toast("Ticket has been resolved");
      resolveTicketConfirmationDialogState.close();
    },
    [
      onUpdate,
      onSkipFutureAlertsChange,
      resolveTicketConfirmationDialogState,
      toast,
    ]
  );

  return (
    <>
      <TicketStatusPickerPopover
        status={ticket.status}
        onChange={handleStatusChange}
        renderTrigger={({ Trigger, triggerRef, isOpen }) => (
          <Trigger
            as={TableCell}
            ref={triggerRef}
            isFocused={isOpen}
            isHoverable
            classNames={{ content: "min-w-0" }}
          >
            <TicketPropertyPill
              className="[&_svg]:size-4"
              icon={statusDetails.icon}
              label={statusDetails.label}
            />
          </Trigger>
        )}
      />
      <ResolveTicketDialog
        isOpen={resolveTicketConfirmationDialogState.isOpen}
        onClose={resolveTicketConfirmationDialogState.close}
        onConfirm={(options) =>
          handleConfirmResolveTicket(options.skipFutureAlerts ?? false)
        }
        ticketUniqueId={ticket.unique_id}
      />
    </>
  );
};
